import React from 'react';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Button, Wrapper, Menu, MenuItem } from 'hellospa/foundation/control/dropdown/index';
import { mixedSymbol } from './utils';
import classnames from 'classnames';
import { Signer } from './types';
import AllSignersSvg from './assets/all-signers-grid.svg';
import { MIXED_TEXT } from './constants';
import styles from './settings-panel/assigned-to.scss';
import Checkmark from './mobile/checkmark';
import SignerAvatar from 'hellospa/foundation/control/signer-avatar';

type Props = {
  onChange: (signerId: any) => void,
  signers: Array<Signer>,
  selectedSignerId?: Signer['id'] | typeof mixedSymbol,
  selectedSignerName?: Signer['name'] | typeof mixedSymbol,
  className?: string,
  shouldAddAllSigners: boolean,
  wrapperDataQaRef: string,
  buttonDataQaRef: string,
  children?: React.ReactNode,
  mobileVariant?: boolean
  fieldCounts?: Record<Signer['id'], number>
}

type SignerItemProps = {
  selectedSignerId: string | number | typeof mixedSymbol
  name: Signer['name']
  id: Signer['id']
  mobileVariant?: boolean
  fieldCounts?: Props['fieldCounts']
}
function SignerItem(props: SignerItemProps) {

  return (
    <React.Fragment>
      {props.mobileVariant
        ? <SignerAvatar signerId={props.id} name={props.name} className={styles.avatar} />
        : <div className={classnames(styles[`assigneeColor-${props.id}`])} />
      }
      <span className={styles.name}>
        {props.name}
        {props.mobileVariant && props.fieldCounts && (
          <span className={styles.fieldCount}>
            <FormattedMessage
              id=""
              description="Number of fields assigned to a user"
              defaultMessage="{fieldCount, number} {fieldCount, plural, one {field} other {fields}}"
              values={{
                fieldCount: props.fieldCounts[props.id] || 0,
              }} />
          </span>
        )}
      </span>
      {props.mobileVariant && (
        <Checkmark className={classnames(styles.checkmark, {
          [styles.active]: props.selectedSignerId === props.id,
        })} />
      )}
    </React.Fragment>
  );

}

const messages = defineMessages({
  allSignersOption: {
    id: '',
    description: 'An option in the signature preview dropdown that lets you see the view across all signers',
    defaultMessage: 'All Signers',
  },
});
export default function SignersDropdown(props: Props) {
  const intl = useIntl();
  const signerId = props.selectedSignerId ? props.selectedSignerId : 'all-signers';

  const buttonContent = React.useMemo(
    () => {
      const { selectedSignerName, selectedSignerId } = props;
      if (selectedSignerName === mixedSymbol || signerId === mixedSymbol) {
        return MIXED_TEXT;
      }

      return (
        <Button data-qa-ref={props.buttonDataQaRef} className={styles.button} >
          {props.shouldAddAllSigners && !props.selectedSignerName && (
            <React.Fragment>
              <img className={styles.assigneeSquare} src={AllSignersSvg} />
              {intl.formatMessage(messages.allSignersOption)}
            </React.Fragment>
          )}

          {selectedSignerName && selectedSignerId === mixedSymbol && (
            <React.Fragment>
              <div className={styles['assigneeColor-disabled']} />
              <span className={styles.name}>
                {selectedSignerName}
              </span>
            </React.Fragment>
          )}

          {selectedSignerName && selectedSignerId && selectedSignerId !== mixedSymbol && (
            <SignerItem
              selectedSignerId={signerId}
              name={selectedSignerName}
              id={selectedSignerId}
              mobileVariant={props.mobileVariant}
              fieldCounts={props.fieldCounts} />
          )}
        </Button>
      );
    }, [intl, props, signerId],
  );

  return (
    <Wrapper
      onSelection={props.onChange}
      data-qa-ref={props.wrapperDataQaRef}
      className={classnames(styles.wrapper, props.className, {
        [styles.mobileVariant]: props.mobileVariant,
      })}
    >
      {buttonContent}
      <Menu className={styles.menu} tag="ul">
        {props.shouldAddAllSigners && (
          <MenuItem className={styles.menuItem} key={'all-signers'} value={'all-signers'} tag="li">
            <img className={styles.assigneeSquare} src={AllSignersSvg} />
            {intl.formatMessage(messages.allSignersOption)}
          </MenuItem>
        )}
        {props.signers.map((s) => (
          <MenuItem className={styles.menuItem} key={s.id} value={s.id} tag="li">
            <SignerItem
              selectedSignerId={signerId}
              name={s.name}
              id={s.id}
              mobileVariant={props.mobileVariant}
              fieldCounts={props.fieldCounts} />
          </MenuItem>
        ))}
        {props.children}
      </Menu>
    </Wrapper>
  );
}
